import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  Activity,
  Loader2,
  BookOpen,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const data = await apiClient.get('/api/dashboard/stats');
      setStats(data);
    } catch (error) {
      toast.error('Грешка при зареждане на статистики');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Публикувани имоти',
      value: stats?.properties?.published || 0,
      total: stats?.properties?.total || 0,
      icon: Building2,
      color: 'from-[#D4AF37] to-[#B8892E]',
      trend: `${stats?.properties?.featured || 0} featured`
    },
    {
      title: 'Анализи & Новини',
      value: stats?.blog?.published || 0,
      total: stats?.blog?.total || 0,
      icon: BookOpen,
      color: 'from-[#B8892E] to-[#D4AF37]',
      trend: 'Публикувани'
    },
    {
      title: 'Нови лидове',
      value: stats?.leads?.new || 0,
      total: stats?.leads?.total || 0,
      icon: Users,
      color: 'from-[#F3D98B] to-[#D4AF37]',
      trend: '+' + (stats?.leads?.new_this_week || 0) + ' тази седмица'
    },
    {
      title: 'Market Briefs',
      value: stats?.market_briefs?.published || 0,
      total: stats?.market_briefs?.total || 0,
      icon: TrendingUp,
      color: 'from-[#D4AF37] to-[#F3D98B]',
      trend: 'Активни'
    }
  ];

  const quickActions = [
    { label: 'Добави имот', icon: Building2, to: '/admin/properties' },
    { label: 'Нова статия', icon: BookOpen, to: '/admin/news' },
    { label: 'Media Library', icon: Activity, to: '/admin/media' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8] mb-2">
          Dashboard
        </h1>
        <p className="text-[#A9A9A9]">
          Преглед на активността и статистики
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="dashboard-stats">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-[#0B0B0B]" />
              </div>
              <span className="text-xs text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded">
                {stat.trend}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-[#F6F2E8] mb-1">
              {stat.value}
            </h3>
            <p className="text-sm text-[#A9A9A9]">
              {stat.title}
            </p>
            <p className="text-xs text-[#D4AF37] mt-2">
              от общо {stat.total}
            </p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6">
        <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">Бързи действия</h2>
        <div className="flex flex-wrap gap-3">
          {quickActions.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="flex items-center gap-2 px-4 py-2 bg-[#0B0B0B] border border-[#D4AF37]/20 rounded-lg hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] text-[#F6F2E8] text-sm transition-colors"
              data-testid={`quick-action-${q.to.split('/').pop()}`}
            >
              <Plus size={14} className="text-[#D4AF37]" />
              <q.icon size={14} />
              <span>{q.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Leads by Type */}
      <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6">
        <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-6">
          Лидове по тип клиент
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(stats?.leads?.by_client_type || {}).map(([type, count]) => (
            <div key={type} className="bg-[#0B0B0B] rounded-lg p-4 border border-[#D4AF37]/10">
              <p className="text-2xl font-bold text-[#D4AF37] mb-1">{count}</p>
              <p className="text-sm text-[#A9A9A9] capitalize">{type}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6">
          <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-6">
            Последни лидове
          </h2>
          <div className="space-y-4">
            {stats?.recent_activity?.leads?.slice(0, 5).map((lead) => (
              <div
                key={lead._id}
                className="flex items-center justify-between p-3 bg-[#0B0B0B] rounded-lg border border-[#D4AF37]/5 hover:border-[#D4AF37]/20 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-[#F6F2E8]">{lead.name}</p>
                  <p className="text-xs text-[#A9A9A9]">{lead.interest}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  lead.status === 'new' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#A9A9A9]/20 text-[#A9A9A9]'
                }`}>
                  {lead.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Properties */}
        <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6">
          <h2 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-6">
            Последни имоти
          </h2>
          <div className="space-y-4">
            {stats?.recent_activity?.properties?.slice(0, 5).map((property) => (
              <div
                key={property._id}
                className="flex items-center justify-between p-3 bg-[#0B0B0B] rounded-lg border border-[#D4AF37]/5 hover:border-[#D4AF37]/20 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-[#F6F2E8]">{property.title}</p>
                  <p className="text-xs text-[#A9A9A9]">{property.property_type}</p>
                </div>
                <p className="text-sm font-semibold text-[#D4AF37]">
                  {(property.price || 0).toLocaleString()} €
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
